// @ts-nocheck
import { useState, useRef, useMemo } from "react";
import { useDiscovery, publicIntakeUrl, humanizeDiscoveryError, summarizeQuantumSync } from "./discoveryData";

/*
  ══════════════════════════════════════════════════════════════════════════
  SQUARE CODE — CUSTOMER DISCOVERY WORKROOM (v2 of the intake redesign)

  PHASE_2M P9 — WIRED TO THE REAL INTAKE BACKEND:
    • Invites load ONLY from sq_intake_invites via
      GET /api/square/command-center/intake-invites (useDiscovery hook —
      no seeded rows, honest loading / error / auth-required states).
    • Submissions load ONLY from sq_support_requests via
      GET .../intake-invites/submissions.
    • Send to Client → POST intake-invites (real row + public_path; the
      message is stored as the invite description and renders on the real
      public portal at /square/intake/:code).
    • Revoke → POST intake-invites { action: 'revoke' } (status → closed).
    • Mark in Review / Resolve / Close → POST .../submissions
      { decision: 'pending' | 'resolved' | 'closed' }.
    • Convert to Project → POST .../submissions
      { action: 'convert_to_project' } → real sq_projects row; navigation
      offered through the onNavigate prop.
    • Copy link → real clipboard write of window.location.origin +
      public_path, built at click time (SSR-safe).
    • Every mutation above is audited server-side (olv audit trail):
      intake_invite_created / intake_invite_revoked /
      intake_submission_decided / intake_submission_converted_to_project.

  STILL HONESTLY DEFERRED (labeled in place, never faked):
    • No email/SMS is ever sent — link delivery is manual copy/share
      (EMAIL_PROVIDER_REQUIRED). The invite row IS created for real.
    • Push-update messages to an existing workspace have NO backend — the
      portal shows the stored invite description only (SETUP REQUIRED).
    • Private Notes ("Discovery Thread") are session-only — persisting
      them needs a discovery_notes table that is not built.
    • Link-copied / portal-opened / session telemetry is not tracked —
      needs portal beacon events (PLANNED). Only created/updated/submission
      counts from sq_intake_invites are shown.
    • File storage stays STORAGE PENDING (attachment metadata only).
    • The bottom command composer's agent bridge is PLANNED (not wired).

  DESIGN (unchanged by the wiring pass):
    • No emoji anywhere — every icon is a small animated SVG.
    • "Customer discovery" language in user-facing copy; real backend
      truth (routes, table names) verbatim in the technical footnotes.
    • Send-off model: the operator names the client, picks a project
      type, writes the message; sending creates the client's private
      workspace link and the message is what they see there.
    • Manage Invites rows click through to a full Client Workspace view;
      the right rail there is the invite's lifecycle panel.
    • Every button is a single flat outline style (no gradients).
  ══════════════════════════════════════════════════════════════════════════
*/


const C = { CYAN:"#0891b2", PURPLE:"#7c3aed", TEAL:"#14b8a6", AMBER:"#f59e0b", GREEN:"#059669", RED:"#dc2626", BLUE:"#2563eb" };
const INK = { primary:"#e4e8f0", secondary:"#c0c8d6", tertiary:"#8a94a8", muted:"#6a7488", faint:"#4a5262", whisper:"#333c4c" };
const FONT = "'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";
const MONO = "'SF Mono', 'JetBrains Mono', ui-monospace, monospace";
function a(hex, pct){ const v = Math.round((pct/100)*255).toString(16).padStart(2,"0"); return `${hex}${v}`; }

const STATE_META = {
  powered:         { color:C.GREEN,  label:"POWERED" },
  sealed:          { color:C.TEAL,   label:"SEALED" },
  local_preview:   { color:C.CYAN,   label:"LOCAL PREVIEW" },
  local_state:     { color:C.BLUE,   label:"LOCAL STATE" },
  seal_pending:    { color:C.TEAL,   label:"SEAL PENDING" },
  backend_ready:   { color:C.GREEN,  label:"BACKEND READY" },
  storage_pending: { color:C.AMBER,  label:"STORAGE PENDING" },
  setup_required:  { color:C.AMBER,  label:"SETUP REQUIRED" },
  planned:         { color:C.PURPLE, label:"PLANNED" },
  blocked:         { color:C.RED,    label:"BLOCKED" },
};
function Badge({ state, small }){
  const m = STATE_META[state];
  return <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize: small?7.5:8.5, fontWeight:800, letterSpacing:"0.06em", color:m.color, fontFamily:MONO, padding: small?"2px 6px":"3px 8px", borderRadius:6, background:a(m.color,10), border:`1px solid ${a(m.color,24)}` }}>
    <span style={{width:5,height:5,borderRadius:"50%",background:m.color}}/>{m.label}
  </span>;
}
// LIVE badge — used only for flows genuinely wired to the real backend.
// Single text node so the exact "LIVE · <source>" string is assertable.
function LiveBadge({ source, small }){
  return <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize: small?7.5:8.5, fontWeight:800, letterSpacing:"0.06em", color:C.GREEN, fontFamily:MONO, padding: small?"2px 6px":"3px 8px", borderRadius:6, background:a(C.GREEN,10), border:`1px solid ${a(C.GREEN,24)}` }}>
    <span style={{width:5,height:5,borderRadius:"50%",background:C.GREEN}}/>{`LIVE · ${source}`}
  </span>;
}

// ── Real-row display helpers (sq_intake_invites / sq_support_requests) ──
function fmtDate(iso){
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" }); }
  catch { return String(iso); }
}
function fmtDay(iso){
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString([], { month:"short", day:"numeric" }); }
  catch { return String(iso); }
}
// Display status for an invite row: the backend vocabulary is
// active | closed (revoke sets closed); expiry is derived client-side
// exactly like the production Client Intake surface does.
function inviteDisplayStatus(inv){
  if (inv.status === "closed") return "revoked";
  if (inv.status === "active" && inv.expires_at && new Date(inv.expires_at).getTime() < Date.now()) return "expired";
  return inv.status;
}

// ══════════════════════════════════════════════════════════════════════════
// ICONS — animated SVG, no emoji anywhere
// ══════════════════════════════════════════════════════════════════════════
function IconWebsite({ size=40, color=C.AMBER }){
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect x="4" y="6" width="32" height="28" rx="4" stroke={color} strokeWidth="1.6" fill={color} fillOpacity="0.06"/>
      <line x1="4" y1="14" x2="36" y2="14" stroke={color} strokeWidth="1.2" opacity="0.45"/>
      <circle cx="9" cy="10" r="1.3" fill={color} opacity="0.85"/>
      <circle cx="13.5" cy="10" r="1.3" fill={color} opacity="0.5"/>
      <circle cx="18" cy="10" r="1.3" fill={color} opacity="0.3"/>
      <line x1="9" y1="20" x2="28" y2="20" stroke={color} strokeWidth="1.1" opacity="0.35"/>
      <line x1="9" y1="24.5" x2="22" y2="24.5" stroke={color} strokeWidth="1.1" opacity="0.25"/>
      <rect x="9" y="29" width="4" height="2.4" fill={color} style={{animation:"icon-blink 1.6s ease-in-out infinite"}}/>
    </svg>
  );
}
function IconCode({ size=40, color=C.TEAL }){
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect x="4" y="5" width="32" height="30" rx="4" stroke={color} strokeWidth="1.6" fill={color} fillOpacity="0.06"/>
      <line x1="9" y1="12" x2="18" y2="12" stroke={color} strokeWidth="1.3" opacity="0.55"/>
      <line x1="9" y1="17" x2="24" y2="17" stroke={color} strokeWidth="1.3" opacity="0.4"/>
      <line x1="12" y1="22" x2="26" y2="22" stroke={color} strokeWidth="1.3" opacity="0.4"/>
      <line x1="9" y1="27" x2="16" y2="27" stroke={color} strokeWidth="1.3" opacity="0.55"/>
      <rect x="17" y="26" width="4" height="2.2" fill={color} style={{animation:"icon-blink 1.4s ease-in-out infinite"}}/>
    </svg>
  );
}
function IconPresentation({ size=40, color=C.BLUE }){
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect x="5" y="8" width="30" height="20" rx="3" stroke={color} strokeWidth="1.6" fill={color} fillOpacity="0.06"/>
      <line x1="16" y1="32" x2="24" y2="32" stroke={color} strokeWidth="1.4" opacity="0.5"/>
      <line x1="20" y1="28" x2="20" y2="32" stroke={color} strokeWidth="1.4" opacity="0.5"/>
      <circle cx="20" cy="18" r="6" stroke={color} strokeWidth="1.3" opacity="0.35" style={{transformOrigin:"20px 18px",animation:"icon-pulse-ring 2.2s ease-out infinite"}}/>
      <path d="M18 15 L23 18 L18 21 Z" fill={color} opacity="0.85"/>
    </svg>
  );
}
function IconDocument({ size=40, color=C.PURPLE }){
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect x="10" y="4" width="20" height="32" rx="3" stroke={color} strokeWidth="1.6" fill={color} fillOpacity="0.06"/>
      <line x1="14" y1="12" x2="26" y2="12" stroke={color} strokeWidth="1.2" opacity="0.5"/>
      <line x1="14" y1="17" x2="26" y2="17" stroke={color} strokeWidth="1.2" opacity="0.4"/>
      <line x1="14" y1="22" x2="22" y2="22" stroke={color} strokeWidth="1.2" opacity="0.4"/>
      <circle cx="14.5" cy="28" r="1.4" fill={color} style={{animation:"icon-blink 1.8s ease-in-out infinite"}}/>
      <line x1="18" y1="28" x2="24" y2="28" stroke={color} strokeWidth="1.2" opacity="0.35"/>
    </svg>
  );
}
function IconLink({ size=14, color=C.CYAN }){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M9 15l6-6" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M11 6h3a4 4 0 010 8h-2" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M13 18h-3a4 4 0 010-8h2" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}
function IconEye({ size=14, color=C.CYAN }){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" stroke={color} strokeWidth="1.6"/>
      <circle cx="12" cy="12" r="2.6" stroke={color} strokeWidth="1.6"/>
    </svg>
  );
}
function IconRevoke({ size=14, color=C.RED }){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.7"/>
      <line x1="6.5" y1="17.5" x2="17.5" y2="6.5" stroke={color} strokeWidth="1.7"/>
    </svg>
  );
}
function IconSparkle({ size=13, color=C.CYAN }){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{animation:"icon-rotate 5s linear infinite", transformOrigin:"center"}}>
      <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8z" fill={color} opacity="0.9"/>
    </svg>
  );
}
function IconCheck({ size=24, color=C.TEAL }){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5" opacity="0.4"/>
      <path d="M7 12.5l3 3 7-7" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray="16" strokeDashoffset="16" style={{animation:"icon-draw 0.6s ease-out 0.15s forwards"}}/>
    </svg>
  );
}
function IconPaperclip({ size=14, color=C.AMBER }){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M17 7.5l-7.5 7.5a3 3 0 004.2 4.2l7-7a5 5 0 00-7-7l-7.2 7.2a4 4 0 005.6 5.6" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}
function IconAgent({ size=15, color=C.PURPLE }){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3" fill={color}/>
      <circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1.2" opacity="0.4" style={{transformOrigin:"12px 12px", animation:"icon-rotate 6s linear infinite"}} strokeDasharray="4 4"/>
    </svg>
  );
}

// ── Project-type cards (create-form vocabulary; maps to invite_type) ────
const PROJECT_TYPES = [
  { id:"website",  label:"Website",             Icon:IconWebsite,      color:C.AMBER,  desc:"Marketing site, client portal, landing page", api:"website" },
  { id:"software", label:"Software Application", Icon:IconCode,        color:C.TEAL,   desc:"Internal tool, dashboard, custom app", api:"software" },
  { id:"deck",     label:"Presentation",         Icon:IconPresentation,color:C.BLUE,   desc:"Pitch deck, investor brief, proposal", api:"presentation" },
  { id:"other",    label:"Something Else",       Icon:IconDocument,    color:C.PURPLE, desc:"Not sure yet — describe it below", api:"other" },
];
const INVITE_TYPE_META = {
  website:      { label:"Website",              color:C.AMBER },
  software:     { label:"Software Application", color:C.TEAL },
  presentation: { label:"Presentation",         color:C.BLUE },
  other:        { label:"Something Else",       color:C.PURPLE },
  custom:       { label:"Custom",               color:C.PURPLE },
};

// Real audit-trail action names the intake routes write server-side.
// Shown as documentation of the receipt vocabulary — an in-workroom live
// audit feed is not wired, so these are never presented as activity rows.
const AUDIT_ACTIONS = [
  "intake_invite_created", "intake_invite_revoked",
  "intake_submission_decided", "intake_submission_converted_to_project",
];

// ── Shared atoms ────────────────────────────────────────────────────────
function Substrate(){
  return (
    <>
      <svg width="100%" height="100%" style={{position:"fixed",inset:0,opacity:0.03,pointerEvents:"none",zIndex:0}}>
        <defs><pattern id="ig" width="46" height="46" patternUnits="userSpaceOnUse"><line x1="0" y1="0" x2="0" y2="46" stroke="#fff" strokeWidth="0.3"/><line x1="0" y1="0" x2="46" y2="0" stroke="#fff" strokeWidth="0.3"/></pattern></defs>
        <rect width="100%" height="100%" fill="url(#ig)"/>
      </svg>
      <div aria-hidden style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0,background:`radial-gradient(ellipse 60% 40% at 8% -5%, ${a(C.PURPLE,12)} 0%, transparent 55%), radial-gradient(ellipse 55% 40% at 100% 10%, ${a(C.CYAN,9)} 0%, transparent 55%), radial-gradient(ellipse 55% 45% at 50% 108%, ${a(C.TEAL,7)} 0%, transparent 55%)`}}/>
    </>
  );
}
function Kicker({children,style}){ return <div style={{fontSize:9,letterSpacing:"0.22em",color:INK.faint,fontWeight:800,fontFamily:MONO,textTransform:"uppercase",...style}}>{children}</div>; }
function GlowCard({ children, accent=C.CYAN, active, onClick, style }){
  const [h,setH]=useState(false);
  const glow = active || h;
  return (
    <div onClick={onClick} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)} style={{
      borderRadius:16, cursor: onClick?"pointer":"default",
      background: glow ? a(accent,7) : "rgba(255,255,255,0.02)",
      border:`1px solid ${glow ? a(accent,32) : "rgba(255,255,255,0.06)"}`,
      boxShadow: glow ? `0 0 0 1px ${a(accent,10)}, 0 10px 30px ${a(accent,14)}` : "none",
      transition:"all 0.18s", ...style,
    }}>{children}</div>
  );
}
// FLATLINE button — single flat outline tone, no gradients, no solid fills.
function ActionBtn({ children, accent=C.CYAN, onClick, small }){
  return (
    <button onClick={onClick} style={{
      padding: small ? "5px 11px" : "7px 15px", borderRadius:8, cursor:"pointer", fontFamily:FONT,
      fontSize: small?10.5:11.5, fontWeight:700, whiteSpace:"nowrap",
      color: accent, background: a(accent,6), border:`1px solid ${a(accent,26)}`,
    }}>{children}</button>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// TOP BAR
// ══════════════════════════════════════════════════════════════════════════
function TopBar({ queueCount, onBack, onHome }){
  return (
    <div style={{ position:"relative", zIndex:20, flexShrink:0, display:"flex", alignItems:"center", gap:14, padding:"11px 22px", background:"rgba(10,11,16,0.92)", backdropFilter:"blur(18px)", borderBottom:`1px solid ${a(C.CYAN,12)}` }}>
      <button type="button" onClick={onHome} title="Home — back to the Command Center" aria-label="Home — Command Center" style={{ width:28, height:28, borderRadius:8, cursor:"pointer", background:a(C.CYAN,14), border:`1px solid ${a(C.CYAN,26)}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <span style={{fontSize:13,fontWeight:900,color:C.CYAN}}>Q</span>
      </button>
      <div>
        <Kicker>Square Code · Customer Discovery</Kicker>
        <div style={{ fontSize:16, fontWeight:900, color:INK.primary, letterSpacing:"-0.01em", marginTop:1 }}>Customer Discovery — Workroom</div>
      </div>

      <div style={{ width:1, height:24, background:"rgba(255,255,255,0.08)" }}/>
      <span style={{fontSize:10,color:INK.muted,fontFamily:MONO}}>Personal Workspace</span>

      <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
        <LiveBadge source="sq_intake_invites"/>
        <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:10.5, fontWeight:800, color:INK.secondary, fontFamily:MONO, padding:"3px 10px", borderRadius:6, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)" }}>
          Queue <span style={{color:C.AMBER}}>{queueCount}</span>
        </span>
        <button onClick={onBack} style={{ padding:"5px 12px", borderRadius:8, cursor:"pointer", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)", color:INK.muted, fontSize:10.5, fontWeight:700, fontFamily:MONO }}>← Command Center</button>
      </div>
    </div>
  );
}

const MODES = [
  { id:"send",   label:"New Discovery" },
  { id:"create", label:"New Client + Project" },
  { id:"manage", label:"Manage Invites" },
  { id:"queue",  label:"Review Queue" },
];
function ModeTabs({ mode, setMode, queueCount }){
  return (
    <div style={{ display:"flex", gap:3, padding:3, borderRadius:11, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)" }}>
      {MODES.map(m=>{
        const on = mode===m.id;
        return (
          <div key={m.id} onClick={()=>setMode(m.id)} style={{ padding:"7px 16px", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:on?800:600, color:on?C.CYAN:INK.muted, background:on?a(C.CYAN,13):"transparent", display:"flex", alignItems:"center", gap:6, transition:"all 0.15s" }}>
            {m.label}{m.id==="queue" && queueCount>0 && <span style={{fontSize:9,fontWeight:800,color:on?C.CYAN:C.AMBER,background:a(on?C.CYAN:C.AMBER,14),padding:"1px 6px",borderRadius:5}}>{queueCount}</span>}
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// NEW DISCOVERY (formerly Send Intake)
// ══════════════════════════════════════════════════════════════════════════
function DiscoveryThread({ thread, setThread }){
  const [entry, setEntry] = useState("");
  const [who, setWho] = useState("you");
  const add = () => {
    if (!entry.trim()) return;
    setThread(prev => [...prev, { who, text:entry.trim(), time:"just now" }]);
    setEntry("");
  };
  return (
    <GlowCard style={{ padding:"16px 18px", marginBottom:14 }}>
      <Kicker style={{marginBottom:8}}>Private Notes <span style={{color:INK.whisper,fontWeight:600,letterSpacing:0}}>· not sent to the client</span></Kicker>
      <div style={{fontSize:10.5,color:INK.muted,marginBottom:12,lineHeight:1.5}}>Log what you discussed on the call, or anything the client sent you separately — a screenshot over text, a follow-up email. This stays internal, separate from the message above that actually gets posted to their workspace.</div>
      {thread.length>0 && (
        <div style={{marginBottom:10,display:"flex",flexDirection:"column",gap:6}}>
          {thread.map((t,i)=>(
            <div key={i} style={{padding:"8px 11px",borderRadius:9,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)"}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                <span style={{fontSize:9.5,fontWeight:800,color:t.who==="you"?C.CYAN:C.PURPLE}}>{t.who==="you"?"You":"Client (relayed)"}</span>
                <span style={{fontSize:8.5,color:INK.whisper,fontFamily:MONO,marginLeft:"auto"}}>{t.time}</span>
              </div>
              <div style={{fontSize:11.5,color:INK.secondary,lineHeight:1.5}}>{t.text}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{display:"flex",gap:6,marginBottom:6}}>
        <div onClick={()=>setWho("you")} style={{padding:"4px 10px",borderRadius:6,cursor:"pointer",fontSize:9.5,fontWeight:700,color:who==="you"?C.CYAN:INK.muted,background:who==="you"?a(C.CYAN,10):"transparent",border:`1px solid ${who==="you"?a(C.CYAN,22):"rgba(255,255,255,0.07)"}`}}>You</div>
        <div onClick={()=>setWho("client")} style={{padding:"4px 10px",borderRadius:6,cursor:"pointer",fontSize:9.5,fontWeight:700,color:who==="client"?C.PURPLE:INK.muted,background:who==="client"?a(C.PURPLE,10):"transparent",border:`1px solid ${who==="client"?a(C.PURPLE,22):"rgba(255,255,255,0.07)"}`}}>Client (relayed)</div>
      </div>
      <div style={{display:"flex",gap:6}}>
        <input value={entry} onChange={e=>setEntry(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()} placeholder="Add a note to the thread…"
          style={{flex:1,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:8,padding:"8px 11px",color:INK.secondary,fontSize:11.5,fontFamily:FONT,outline:"none"}}/>
        <ActionBtn accent={C.CYAN} onClick={add}>Add</ActionBtn>
      </div>
      <div style={{marginTop:9,fontSize:9,color:INK.whisper,lineHeight:1.5}}>Session-only for now — persisting this thread needs a discovery_notes table. No live client channel exists; this is your own log.</div>
    </GlowCard>
  );
}

function SendIntake({ onSend, generatedInvite, onPreviewPublic, onGoToWorkspace, thread, setThread, authRequired }){
  const [clientName, setClientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [description, setDescription] = useState("");
  const [links, setLinks] = useState("");
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const filled = clientName.trim().length > 0 && !!selectedType && description.trim().length > 0;
  const handleGenerate = async () => {
    if (!filled || sending) return;
    setSending(true); setSendError(null);
    try {
      const type = PROJECT_TYPES.find(t=>t.id===selectedType);
      // Inspiration links travel inside the stored description so they
      // genuinely reach the invite row (no separate links column exists).
      const message = links.trim() ? `${description.trim()}\n\nInspiration links: ${links.trim()}` : description.trim();
      await onSend({
        title: clientName.trim(),
        invite_type: type ? type.api : "other",
        description: message,
        recipientEmail: recipientEmail.trim() || null,
      });
    } catch (err) {
      setSendError(humanizeDiscoveryError(err));
    } finally {
      setSending(false);
    }
  };
  // Absolute URL built at click time from window.location.origin (SSR-safe).
  const copyLink = () => {
    if (!generatedInvite) return;
    const url = publicIntakeUrl(generatedInvite.public_path || generatedInvite.invite_code);
    try { navigator.clipboard?.writeText(url).catch(()=>{}); } catch(e) {}
    setCopied(true); setTimeout(()=>setCopied(false), 1400);
  };
  const openLink = () => {
    if (!generatedInvite) return;
    if (typeof window !== "undefined") window.open(publicIntakeUrl(generatedInvite.public_path || generatedInvite.invite_code), "_blank", "noopener");
  };

  return (
    <div style={{ maxWidth:820, margin:"0 auto" }}>
      <div style={{ fontSize:20, fontWeight:900, color:INK.primary, marginBottom:4 }}>Set Up Their Workspace</div>
      <div style={{ fontSize:12, color:INK.muted, marginBottom:18 }}>Write the message you'd send them — what you're building, what it looks like starting off. Sending it creates their private workspace link for real (an sq_intake_invites row) and your message is what they see when they open it. This isn't a form for them to fill out — it's your send-off.</div>

      {authRequired && (
        <GlowCard accent={C.AMBER} active style={{ padding:"12px 16px", marginBottom:14 }}>
          <div style={{fontSize:11.5,color:INK.secondary,lineHeight:1.6}}>Sign in required — sending creates a real sq_intake_invites row through the authenticated intake API. Nothing is created (and no rows load) without a session.</div>
        </GlowCard>
      )}

      <GlowCard style={{ padding:"16px 18px", marginBottom:14 }}>
        <Kicker style={{marginBottom:8}}>Who's this for?</Kicker>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <input value={clientName} onChange={e=>setClientName(e.target.value)} placeholder="Client or company name"
            style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:9, padding:"9px 12px", color:INK.secondary, fontSize:12.5, fontFamily:FONT, outline:"none" }}/>
          <input value={recipientEmail} onChange={e=>setRecipientEmail(e.target.value)} placeholder="Their email (optional, for later)"
            style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:9, padding:"9px 12px", color:INK.secondary, fontSize:12.5, fontFamily:FONT, outline:"none" }}/>
        </div>
      </GlowCard>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12, marginBottom:18 }}>
        {PROJECT_TYPES.map(t=>{
          const Icon = t.Icon;
          return (
            <GlowCard key={t.id} accent={t.color} active={selectedType===t.id} onClick={()=>setSelectedType(t.id)} style={{ padding:"20px 22px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                <Icon size={40} color={t.color}/>
                <div>
                  <div style={{ fontSize:14.5, fontWeight:800, color: selectedType===t.id ? t.color : INK.secondary }}>{t.label}</div>
                  <div style={{ fontSize:10.5, color:INK.muted, marginTop:2 }}>{t.desc}</div>
                </div>
              </div>
            </GlowCard>
          );
        })}
      </div>

      <GlowCard style={{ padding:"18px 20px", marginBottom:14 }}>
        <Kicker style={{marginBottom:8}}>Message To The Client</Kicker>
        <textarea value={description} onChange={e=>setDescription(e.target.value)} placeholder="What are you building for them, and what will it look like starting off? This is posted as the first message in their workspace — write it like you're telling them, not like you're filling out a form."
          style={{ width:"100%", minHeight:84, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:10, padding:12, color:INK.secondary, fontSize:12.5, fontFamily:FONT, outline:"none", resize:"vertical" }}/>
        <div style={{marginTop:10}}>
          <Kicker style={{marginBottom:6}}>Inspiration links (optional)</Kicker>
          <input value={links} onChange={e=>setLinks(e.target.value)} placeholder="https://…"
            style={{ width:"100%", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:9, padding:"9px 12px", color:INK.secondary, fontSize:12, fontFamily:FONT, outline:"none" }}/>
        </div>
        <div style={{ marginTop:10, padding:"10px 12px", borderRadius:9, background:"rgba(255,255,255,0.02)", border:"1px dashed rgba(255,255,255,0.1)", display:"flex", alignItems:"center", gap:10 }}>
          <IconPaperclip size={15} color={C.AMBER}/>
          <span style={{flex:1,fontSize:11,color:INK.muted}}>Screenshots &amp; files — names/sizes travel with the record</span>
          <Badge state="storage_pending" small/>
        </div>
      </GlowCard>

      <DiscoveryThread thread={thread} setThread={setThread}/>

      <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center", gap:12 }}>
        {sendError && <span style={{fontSize:10.5,color:C.RED,fontFamily:MONO}}>{sendError}</span>}
        <ActionBtn accent={C.TEAL} onClick={handleGenerate}>{sending ? "Creating live invite…" : filled ? "Send to Client →" : "Add a name, type, and message"}</ActionBtn>
      </div>

      {generatedInvite && (
        <GlowCard accent={C.TEAL} active style={{ padding:"18px 20px", marginTop:18 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <Kicker style={{color:a(C.TEAL,75)}}>Workspace Created · {generatedInvite.title}</Kicker>
            <LiveBadge source="sq_intake_invites" small/>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
            <div>
              <div style={{fontSize:9,color:INK.faint,fontFamily:MONO,marginBottom:2}}>CODE</div>
              <div style={{fontSize:16,fontWeight:900,color:C.TEAL,fontFamily:MONO}}>{generatedInvite.invite_code}</div>
            </div>
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:9,color:INK.faint,fontFamily:MONO,marginBottom:2}}>THEIR PRIVATE LINK</div>
              <div style={{fontSize:11.5,color:INK.secondary,fontFamily:MONO,wordBreak:"break-all"}}>{publicIntakeUrl(generatedInvite.public_path || generatedInvite.invite_code)}</div>
            </div>
            <ActionBtn accent={C.TEAL} onClick={copyLink}>{copied ? <span style={{display:"inline-flex",alignItems:"center",gap:5}}>Copied <IconCheck size={12} color={C.TEAL}/></span> : "Copy Link"}</ActionBtn>
            <ActionBtn accent={C.TEAL} onClick={openLink}>Open Link</ActionBtn>
          </div>
          <div style={{display:"flex",gap:16,marginTop:12,flexWrap:"wrap"}}>
            <div onClick={onPreviewPublic} style={{ fontSize:11, color:C.CYAN, fontWeight:700, cursor:"pointer" }}>Preview their starting portal →</div>
            <div onClick={onGoToWorkspace} style={{ fontSize:11, color:C.TEAL, fontWeight:700, cursor:"pointer" }}>Go to their workspace →</div>
          </div>
          <div style={{ marginTop:10, fontSize:9.5, color:INK.whisper, lineHeight:1.5 }}>The invite row is live in sq_intake_invites (audited) and your message is stored as its description — it renders on the real portal page at this link. No email was sent{generatedInvite.recipientEmail ? ` to ${generatedInvite.recipientEmail}` : ""}: the link is created, but delivery is manual copy/share until an email provider is wired. Manage or revoke it any time from Manage Invites.</div>
        </GlowCard>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// NEW CLIENT + PROJECT — the ATOMIC path. One server transaction creates a
// real client (sq_clients), its project (sq_projects, bound by client_id),
// and a persisted quantum-sync receipt confirming every dependency. Fail-
// closed: the sealed success renders ONLY after the server confirms
// sync.binding_confirmed; any failure shows the blocked step and nothing is
// created (the whole chain is rolled back). Composed from this screen's own
// atoms — no @square/ui import (this workroom is self-contained by design).
// ══════════════════════════════════════════════════════════════════════════
const SYNC_STEP_META = {
  verified:  { color:C.GREEN,   label:"VERIFIED" },
  connected: { color:C.TEAL,    label:"CONNECTED" },
  sealed:    { color:C.TEAL,    label:"SEALED" },
  syncing:   { color:C.CYAN,    label:"SYNCING" },
  skipped:   { color:INK.faint, label:"SKIPPED" },
  pending:   { color:INK.muted, label:"PENDING" },
  blocked:   { color:C.RED,     label:"BLOCKED" },
  failed:    { color:C.RED,     label:"BLOCKED" },
};
function syncStepMeta(status){ return SYNC_STEP_META[status] || { color:INK.muted, label:String(status||"").toUpperCase() }; }

// The dependency chain the atomic operation confirms — shown pending before
// running, then replaced by the server's real per-step statuses (same order
// as the sq_client_intake_create RPC's checks array).
const SYNC_CHAIN_PREVIEW = [
  { key:"operator",     label:"Authenticated operator" },
  { key:"organization", label:"Active organization" },
  { key:"client",       label:"Client record" },
  { key:"project",      label:"Project record" },
  { key:"binding",      label:"Client and project bound" },
  { key:"receipt",      label:"Persisted receipt" },
];

function SyncReceipt({ checks, sealed, receiptId, summary }){
  const { done, total } = summarizeQuantumSync(checks);
  return (
    <GlowCard accent={sealed ? C.TEAL : C.CYAN} active style={{ padding:"16px 18px", marginTop:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <Kicker style={{color: sealed ? a(C.TEAL,80) : a(C.CYAN,80)}}>Quantum Synchronization{sealed ? " · Sealed" : ""}</Kicker>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:9.5,color:INK.muted,fontFamily:MONO}}>{done} / {total} synchronized</span>
          <LiveBadge source="sq_quantum_sync_receipts" small/>
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:7}}>
        {checks.map((c,i)=>{
          const m = syncStepMeta(c.status);
          return (
            <div key={c.key||i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 11px",borderRadius:9,background:a(m.color,6),border:`1px solid ${a(m.color,20)}`}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:m.color,flexShrink:0}}/>
              <span style={{fontSize:11.5,color:INK.secondary,fontWeight:600,flex:1}}>{c.label}</span>
              <span style={{fontSize:8.5,fontWeight:800,letterSpacing:"0.06em",color:m.color,fontFamily:MONO}}>{m.label}</span>
            </div>
          );
        })}
      </div>
      {sealed && (
        <div style={{marginTop:11,fontSize:9.5,color:INK.whisper,lineHeight:1.6}}>
          Every dependency was confirmed by the database before this was accepted — the client and project are real rows bound by a foreign key, and this proof is persisted in sq_quantum_sync_receipts (audited).{receiptId ? ` Receipt ${String(receiptId).slice(0,8)}.` : ""} {summary || ""}
        </div>
      )}
    </GlowCard>
  );
}

function CreateClientProject({ onCreate, authRequired, onNavigate }){
  const [clientName, setClientName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectType, setProjectType] = useState("website");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const filled = clientName.trim().length > 0 && projectName.trim().length > 0;
  const sealed = !!(result && result.sync && result.sync.binding_confirmed);
  const checks = result && result.sync && Array.isArray(result.sync.checks) && result.sync.checks.length
    ? result.sync.checks
    : SYNC_CHAIN_PREVIEW.map(s => ({ ...s, status:"pending" }));

  const run = async () => {
    if (!filled || running) return;
    setRunning(true); setError(null); setResult(null);
    try {
      const res = await onCreate({
        client_name: clientName.trim(),
        project_name: projectName.trim(),
        contact_email: contactEmail.trim() || undefined,
        contact_phone: contactPhone.trim() || undefined,
        project_type: projectType,
      });
      setResult(res);
    } catch (err) {
      setError(humanizeDiscoveryError(err));
    } finally {
      setRunning(false);
    }
  };
  const resetForm = () => {
    setClientName(""); setContactEmail(""); setContactPhone(""); setProjectName(""); setProjectType("website");
    setResult(null); setError(null);
  };
  const goToProject = () => {
    const id = result && result.project && result.project.id;
    if (id && onNavigate) onNavigate(`/square/projects/${id}`);
  };

  return (
    <div style={{ maxWidth:820, margin:"0 auto" }}>
      <div style={{ fontSize:20, fontWeight:900, color:INK.primary, marginBottom:4 }}>New Client + Project</div>
      <div style={{ fontSize:12, color:INK.muted, marginBottom:18 }}>Create the client and their first project together, in one atomic step. Nothing is saved until the database confirms every dependency — the client row, the project row, and the foreign-key binding between them — and writes the quantum-sync receipt. If any link fails, the whole thing is rolled back and nothing is created.</div>

      {authRequired && (
        <GlowCard accent={C.AMBER} active style={{ padding:"12px 16px", marginBottom:14 }}>
          <div style={{fontSize:11.5,color:INK.secondary,lineHeight:1.6}}>Sign in required — this creates real sq_clients + sq_projects rows through the authenticated intake API. Nothing is created without a session and an active organization.</div>
        </GlowCard>
      )}

      <GlowCard style={{ padding:"16px 18px", marginBottom:14 }}>
        <Kicker style={{marginBottom:8}}>The client</Kicker>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
          <input value={clientName} onChange={e=>setClientName(e.target.value)} placeholder="Client or company name"
            style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:9, padding:"9px 12px", color:INK.secondary, fontSize:12.5, fontFamily:FONT, outline:"none" }}/>
          <input value={contactEmail} onChange={e=>setContactEmail(e.target.value)} placeholder="Primary contact email (optional)"
            style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:9, padding:"9px 12px", color:INK.secondary, fontSize:12.5, fontFamily:FONT, outline:"none" }}/>
        </div>
        <input value={contactPhone} onChange={e=>setContactPhone(e.target.value)} placeholder="Primary contact phone (optional)"
          style={{ width:"100%", boxSizing:"border-box", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:9, padding:"9px 12px", color:INK.secondary, fontSize:12.5, fontFamily:FONT, outline:"none" }}/>
      </GlowCard>

      <GlowCard style={{ padding:"16px 18px", marginBottom:14 }}>
        <Kicker style={{marginBottom:8}}>Their first project</Kicker>
        <input value={projectName} onChange={e=>setProjectName(e.target.value)} placeholder="Project name"
          style={{ width:"100%", boxSizing:"border-box", marginBottom:10, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:9, padding:"9px 12px", color:INK.secondary, fontSize:12.5, fontFamily:FONT, outline:"none" }}/>
        <Kicker style={{marginBottom:6}}>Type</Kicker>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {PROJECT_TYPES.map(t=>{
            const on = projectType===t.api;
            return (
              <div key={t.id} onClick={()=>setProjectType(t.api)} style={{ padding:"6px 12px", borderRadius:8, cursor:"pointer", fontSize:11, fontWeight: on?800:600, color: on?t.color:INK.muted, background: on?a(t.color,12):"transparent", border:`1px solid ${on?a(t.color,26):"rgba(255,255,255,0.07)"}` }}>{t.label}</div>
            );
          })}
        </div>
      </GlowCard>

      <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center", gap:12 }}>
        {error && <span style={{fontSize:10.5,color:C.RED,fontFamily:MONO}}>{error}</span>}
        <ActionBtn accent={sealed ? C.TEAL : C.CYAN} onClick={sealed ? resetForm : run}>
          {running ? "Synchronizing…" : sealed ? "Create another" : filled ? "Create client + project →" : "Add a client and project name"}
        </ActionBtn>
      </div>

      {error && (
        <GlowCard accent={C.RED} active style={{ padding:"14px 18px", marginTop:16 }}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <Kicker style={{color:a(C.RED,80)}}>Synchronization Blocked</Kicker>
            <Badge state="blocked" small/>
          </div>
          <div style={{fontSize:11.5,color:INK.secondary,lineHeight:1.6}}>{error} — nothing was created. The operation is atomic and fail-closed: because a dependency could not be confirmed, the whole chain was rolled back. Fix the issue and run it again.</div>
        </GlowCard>
      )}

      {(result || running) && !error && (
        <SyncReceipt checks={checks} sealed={sealed} receiptId={result && result.receipt && result.receipt.id} summary={result && result.receipt && result.receipt.summary}/>
      )}

      {sealed && (
        <div style={{ display:"flex", justifyContent:"flex-end", gap:12, marginTop:12 }}>
          <ActionBtn accent={C.TEAL} onClick={goToProject}>Go to the project →</ActionBtn>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MANAGE INVITES
// ══════════════════════════════════════════════════════════════════════════
const INVITE_FILTERS = ["all","active","expired","revoked"];
const INVITE_STATUS_COLOR = { active:C.GREEN, expired:INK.faint, revoked:C.RED, closed:C.RED };

function ManageInvites({ invites, onOpen, onRevoke, onCopyLink, onViewSubmissions }){
  const [filter, setFilter] = useState("all");
  const rows = filter==="all" ? invites : invites.filter(i=>inviteDisplayStatus(i)===filter);

  return (
    <div style={{ maxWidth:900, margin:"0 auto" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ fontSize:20, fontWeight:900, color:INK.primary }}>Manage Invites</div>
        <div style={{ display:"flex", gap:2, padding:2, borderRadius:9, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)" }}>
          {INVITE_FILTERS.map(f=>(
            <div key={f} onClick={()=>setFilter(f)} style={{ padding:"5px 12px", borderRadius:7, cursor:"pointer", fontSize:10.5, fontWeight: filter===f?800:500, textTransform:"capitalize", color: filter===f?C.CYAN:INK.muted, background: filter===f?a(C.CYAN,13):"transparent" }}>{f}</div>
          ))}
        </div>
      </div>

      <GlowCard style={{ overflow:"hidden" }}>
        <div style={{ display:"grid", gridTemplateColumns:"150px 1fr 90px 90px 70px 90px 90px 90px", gap:8, padding:"9px 16px", fontSize:9, fontWeight:800, color:INK.faint, letterSpacing:"0.05em", fontFamily:MONO, borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
          <span>CODE</span><span>TARGET</span><span>CREATED</span><span>EXPIRES</span><span>SUBS</span><span>STATUS</span><span>UPDATED</span><span>ACTIONS</span>
        </div>
        {rows.length===0 ? (
          <div style={{padding:"28px 16px",textAlign:"center",fontSize:11.5,color:INK.muted}}>{invites.length===0 ? "No invites yet — create one from New Discovery. This list reads sq_intake_invites directly; nothing is fabricated." : "No invites match this filter."}</div>
        ) : rows.map((inv,i)=>{
          const status = inviteDisplayStatus(inv);
          return (
            <div key={inv.id} onClick={()=>onOpen(inv.invite_code)} title="Open this client's workspace" style={{ display:"grid", gridTemplateColumns:"150px 1fr 90px 90px 70px 90px 90px 90px", gap:8, alignItems:"center", padding:"11px 16px", cursor:"pointer", borderBottom: i<rows.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}
              onMouseEnter={e=>e.currentTarget.style.background=a(C.CYAN,5)} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <span style={{fontSize:10,fontWeight:800,color:C.CYAN,fontFamily:MONO,overflow:"hidden",textOverflow:"ellipsis"}}>{inv.invite_code}</span>
              <span style={{fontSize:11.5,color:INK.secondary,fontWeight:600}}>{inv.title}</span>
              <span style={{fontSize:10,color:INK.muted,fontFamily:MONO}}>{fmtDay(inv.created_at)}</span>
              <span style={{fontSize:10,color:INK.muted,fontFamily:MONO}}>{inv.expires_at ? fmtDay(inv.expires_at) : "never"}</span>
              <span style={{fontSize:11,color:INK.secondary,fontFamily:MONO}}>{Number(inv.submission_count ?? 0)}</span>
              <span style={{fontSize:8.5,fontWeight:800,color:INVITE_STATUS_COLOR[status] || INK.muted,textTransform:"uppercase",fontFamily:MONO}}>{status}</span>
              <span style={{fontSize:10,color:INK.whisper,fontFamily:MONO}}>{fmtDay(inv.updated_at)}</span>
              <span style={{display:"flex",gap:8,alignItems:"center"}} onClick={e=>e.stopPropagation()}>
                <span onClick={()=>onCopyLink(inv.invite_code)} title="Copy link" style={{cursor:"pointer",display:"flex"}}><IconLink size={13} color={C.CYAN}/></span>
                <span onClick={()=>onViewSubmissions(inv.invite_code)} title="View submissions in the Review Queue" style={{cursor:"pointer",display:"flex"}}><IconEye size={13} color={INK.muted}/></span>
                {status==="active" && <span onClick={()=>onRevoke(inv.id, inv.invite_code)} title="Revoke — the public link stops accepting submissions" style={{cursor:"pointer",display:"flex"}}><IconRevoke size={13} color={C.RED}/></span>}
              </span>
            </div>
          );
        })}
      </GlowCard>
      <div style={{marginTop:10,fontSize:10.5,color:INK.muted}}>Click any row to open that client's full workspace — stored message, live link controls, and lifecycle panel.</div>
    </div>
  );
}

// ── Invite lifecycle panel — only real sq_intake_invites fields; the
//    copy/open/session telemetry that has no backend is labeled PLANNED,
//    never rendered as fabricated timestamps. ──────────────────────────
function InviteTimeline({ inv }){
  const status = inviteDisplayStatus(inv);
  const rows = [
    { label:"Invite created", value:fmtDate(inv.created_at), color:C.GREEN },
    { label:"Last updated", value:fmtDate(inv.updated_at), color:C.CYAN },
    { label:"Submissions received", value:String(Number(inv.submission_count ?? 0)), color:C.CYAN },
    { label:"Expires", value: inv.expires_at ? fmtDate(inv.expires_at) : "never", color:INK.muted },
    { label:"Status", value:status, color:INVITE_STATUS_COLOR[status] || INK.muted },
  ];
  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
        <Kicker>Lifecycle</Kicker>
        <LiveBadge source="sq_intake_invites" small/>
      </div>
      <div style={{fontSize:9.5,color:INK.secondary,fontFamily:MONO,marginBottom:10,wordBreak:"break-all"}}>{inv.invite_code}</div>
      {rows.map((r,i)=>(
        <div key={i} style={{display:"flex",gap:9,marginBottom:2}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:r.color,flexShrink:0,marginTop:2}}/>
            {i<rows.length-1 && <div style={{width:1,flex:1,minHeight:18,background:"rgba(255,255,255,0.1)"}}/>}
          </div>
          <div style={{flex:1,paddingBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:INK.secondary}}>{r.label}</div>
            <div style={{fontSize:9.5,color:INK.muted,fontFamily:MONO,marginTop:1}}>{r.value}</div>
          </div>
        </div>
      ))}

      <div style={{marginTop:4,marginBottom:12,display:"flex",alignItems:"flex-start",gap:6}}>
        <Badge state="planned" small/>
        <span style={{fontSize:8.5,color:INK.whisper,lineHeight:1.4}}>link-copied / portal-opened / session telemetry is not tracked — it needs portal beacon events. Nothing here is simulated.</span>
      </div>

      <div style={{ paddingTop:12, borderTop:"1px solid rgba(255,255,255,0.06)" }}>
        <div style={{fontSize:9.5,color:INK.whisper,lineHeight:1.55}}>Create and revoke on this invite are audited server-side to the olv audit trail (intake_invite_created / intake_invite_revoked). An in-workroom audit feed is not wired yet.</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CLIENT WORKSPACE — what opens when you click into a Manage Invites row.
// Message log (what they see) + push-update composer + linked submissions.
// Lifecycle timeline lives in the right rail for this mode.
// ══════════════════════════════════════════════════════════════════════════
function MessageBubble({ m }){
  return (
    <div style={{padding:"10px 13px",borderRadius:10,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
        <span style={{fontSize:9.5,fontWeight:800,color:C.CYAN}}>You → their portal</span>
        <span style={{fontSize:8.5,color:INK.whisper,fontFamily:MONO,marginLeft:"auto"}}>{m.time}</span>
      </div>
      <div style={{fontSize:12,color:INK.secondary,lineHeight:1.6}}>{m.text}</div>
    </div>
  );
}

function InviteWorkspace({ invite, onBack, onRevoke, onCopyLink, submissions, onOpenSubmission }){
  const [copied, setCopied] = useState(false);
  const status = inviteDisplayStatus(invite);
  const link = publicIntakeUrl(invite.invite_code);
  const linkedSubs = submissions.filter(s=>s.invite && s.invite.invite_code===invite.invite_code);

  const copy = () => {
    onCopyLink(invite.invite_code);
    setCopied(true); setTimeout(()=>setCopied(false), 1400);
  };

  return (
    <div style={{ maxWidth:780, margin:"0 auto" }}>
      <div onClick={onBack} style={{ display:"inline-block", marginBottom:14, fontSize:11, fontWeight:700, color:INK.muted, cursor:"pointer" }}>← Back to Manage Invites</div>

      <GlowCard accent={C.CYAN} active style={{ padding:"20px 24px", marginBottom:14 }}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8}}>
          <div>
            <Kicker>Client Workspace · {invite.invite_code}</Kicker>
            <div style={{fontSize:19,fontWeight:900,color:INK.primary,marginTop:4}}>{invite.title}</div>
          </div>
          <span style={{fontSize:8.5,fontWeight:800,color:INVITE_STATUS_COLOR[status] || INK.muted,textTransform:"uppercase",fontFamily:MONO,padding:"3px 9px",borderRadius:6,background:a(INVITE_STATUS_COLOR[status] || INK.muted,10)}}>{status}</span>
        </div>
        <div style={{fontSize:11.5,color:INK.tertiary,fontFamily:MONO,marginBottom:12,wordBreak:"break-all"}}>{link}</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <ActionBtn accent={C.CYAN} small onClick={copy}>{copied ? "Copied" : "Copy Link"}</ActionBtn>
          <ActionBtn accent={C.CYAN} small onClick={()=>{ if (typeof window!=="undefined") window.open(publicIntakeUrl(invite.invite_code),"_blank","noopener"); }}>Open Portal</ActionBtn>
          {status==="active" && <ActionBtn accent={C.RED} small onClick={()=>onRevoke(invite.id, invite.invite_code)}>Revoke</ActionBtn>}
        </div>
      </GlowCard>

      <GlowCard style={{ padding:"16px 18px", marginBottom:14 }}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
          <Kicker>Stored Message <span style={{color:INK.whisper,fontWeight:600,letterSpacing:0}}>· what they see when they open the link</span></Kicker>
          <LiveBadge source="sq_intake_invites" small/>
        </div>
        {invite.description ? (
          <div style={{marginBottom:10}}><MessageBubble m={{ text: invite.description, time: fmtDate(invite.created_at) }}/></div>
        ) : (
          <div style={{fontSize:11,color:INK.muted,marginBottom:10}}>No message stored on this invite — its portal shows the title only.</div>
        )}
        <div style={{display:"flex",alignItems:"flex-start",gap:8,marginTop:4}}>
          <Badge state="setup_required" small/>
          <span style={{fontSize:9,color:INK.whisper,lineHeight:1.5}}>Pushing further updates to this portal has no backend yet — the page shows the stored description only. To change what they see, revoke this link and send a fresh one. Notifying the client by email/SMS also stays setup-required until a provider is connected.</span>
        </div>
      </GlowCard>

      {linkedSubs.length>0 && (
        <GlowCard style={{ padding:"16px 18px" }}>
          <Kicker style={{marginBottom:10}}>Submissions From This Workspace</Kicker>
          {linkedSubs.map(s=>(
            <div key={s.id} onClick={()=>onOpenSubmission(s.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",borderRadius:8,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",marginBottom:6,cursor:"pointer"}}>
              <span style={{fontSize:11.5,color:INK.secondary,fontWeight:700}}>{s.subject}</span>
              <span style={{marginLeft:"auto",fontSize:9,color:INK.whisper,fontFamily:MONO}}>{fmtDate(s.created_at)}</span>
            </div>
          ))}
        </GlowCard>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// REVIEW QUEUE + SUBMISSION DETAIL — unchanged structurally, buttons flattened
// ══════════════════════════════════════════════════════════════════════════
// sq_support_requests status vocabulary: 'open' on arrival, then the
// decide action sets 'pending' (in review) | 'resolved' | 'closed'.
const QUEUE_FILTERS = ["open","pending","resolved","closed"];
const QUEUE_STATUS_COLOR = { open:C.AMBER, pending:C.CYAN, resolved:C.GREEN, closed:INK.faint };
const QUEUE_STATUS_LABEL = { open:"New", pending:"In Review", resolved:"Resolved", closed:"Closed" };

function ReviewQueue({ submissions, onOpen }){
  const [filter, setFilter] = useState("open");
  const rows = submissions.filter(s=>s.status===filter);
  return (
    <div style={{ maxWidth:820, margin:"0 auto" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ fontSize:20, fontWeight:900, color:INK.primary }}>Review Queue</div>
        <div style={{ display:"flex", gap:2, padding:2, borderRadius:9, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)" }}>
          {QUEUE_FILTERS.map(f=>(
            <div key={f} onClick={()=>setFilter(f)} style={{ padding:"5px 12px", borderRadius:7, cursor:"pointer", fontSize:10.5, fontWeight: filter===f?800:500, color: filter===f?C.CYAN:INK.muted, background: filter===f?a(C.CYAN,13):"transparent" }}>{QUEUE_STATUS_LABEL[f]}</div>
          ))}
        </div>
      </div>
      {rows.length===0 ? (
        // Empty state — spatial, not boxed: explanatory copy breathes on the
        // ambient canvas instead of sitting inside an opaque card.
        <div style={{padding:"64px 24px",textAlign:"center",maxWidth:440,margin:"0 auto"}}>
          <div style={{fontSize:14,fontWeight:700,color:INK.secondary,lineHeight:1.6}}>{submissions.length===0 ? "No submissions yet" : `Nothing in “${QUEUE_STATUS_LABEL[filter]}” right now`}</div>
          <div style={{fontSize:11.5,color:INK.muted,lineHeight:1.7,marginTop:8}}>{submissions.length===0 ? "Share an invite link — public submissions land in sq_support_requests and appear here." : "Try another filter above."}</div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {rows.map(s=>(
            <GlowCard key={s.id} accent={C.CYAN} onClick={()=>onOpen(s.id)} style={{ padding:"14px 18px" }}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:5}}>
                <span style={{fontSize:12.5,fontWeight:800,color:INK.primary}}>{(s.submitter && s.submitter.name) || "Unknown submitter"}</span>
                {s.invite && <span style={{fontSize:9,fontWeight:800,color:C.CYAN,background:a(C.CYAN,10),padding:"1px 7px",borderRadius:5}}>{s.invite.title}</span>}
                <span style={{marginLeft:"auto",fontSize:9.5,color:INK.whisper,fontFamily:MONO}}>{fmtDate(s.created_at)}</span>
              </div>
              <div style={{fontSize:12,color:INK.secondary,marginBottom:5}}>{s.subject}</div>
              <div style={{fontSize:9,color:INK.faint,fontFamily:MONO}}>invite {s.invite ? s.invite.invite_code : "—"}</div>
            </GlowCard>
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionDetail({ submission, onBack, onUpdateStatus, onConvert, busy, flash }){
  const s = submission;
  const submitter = s.submitter || {};
  const answers = submitter.answers && typeof submitter.answers === "object" ? Object.entries(submitter.answers) : [];
  const attachments = Array.isArray(submitter.attachments) ? submitter.attachments : [];
  const [copied, setCopied] = useState(false);
  const copySummary = () => {
    const txt = `${submitter.name || "Unknown submitter"} · ${s.subject}\n${s.body || ""}`;
    try { navigator.clipboard?.writeText(txt).catch(()=>{}); } catch(e) {}
    setCopied(true); setTimeout(()=>setCopied(false), 1400);
  };
  return (
    <div style={{ maxWidth:780, margin:"0 auto" }}>
      <div onClick={onBack} style={{ display:"inline-block", marginBottom:14, fontSize:11, fontWeight:700, color:INK.muted, cursor:"pointer" }}>← Back to queue</div>

      <GlowCard accent={C.CYAN} active style={{ padding:"22px 26px", marginBottom:14 }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:14 }}>
          <div>
            <Kicker>Submission · {s.invite ? s.invite.invite_code : "—"}</Kicker>
            <div style={{ fontSize:19, fontWeight:900, color:INK.primary, marginTop:4 }}>{s.subject}</div>
          </div>
          <span style={{fontSize:9,fontWeight:800,color:QUEUE_STATUS_COLOR[s.status] || INK.muted,textTransform:"uppercase",fontFamily:MONO,padding:"3px 9px",borderRadius:6,background:a(QUEUE_STATUS_COLOR[s.status] || INK.muted,10)}}>{QUEUE_STATUS_LABEL[s.status] || s.status}</span>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16, fontSize:11.5 }}>
          <div><div style={{color:INK.faint,fontSize:9,fontFamily:MONO,marginBottom:2}}>SUBMITTER</div><div style={{color:INK.secondary,fontWeight:700}}>{submitter.name || "—"}</div></div>
          <div><div style={{color:INK.faint,fontSize:9,fontFamily:MONO,marginBottom:2}}>EMAIL</div><div style={{color:INK.secondary,fontFamily:MONO}}>{submitter.email || "—"}</div></div>
          <div><div style={{color:INK.faint,fontSize:9,fontFamily:MONO,marginBottom:2}}>VIA INVITE</div><div style={{color:INK.secondary,fontWeight:700}}>{s.invite ? s.invite.title : "—"}</div></div>
          <div><div style={{color:INK.faint,fontSize:9,fontFamily:MONO,marginBottom:2}}>RECEIVED</div><div style={{color:INK.secondary,fontFamily:MONO}}>{fmtDate(s.created_at)}</div></div>
        </div>

        <div style={{marginBottom:14}}>
          <div style={{color:INK.faint,fontSize:9,fontFamily:MONO,marginBottom:5}}>DESCRIPTION</div>
          <div style={{fontSize:12.5,color:INK.secondary,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{s.body || "—"}</div>
        </div>

        {answers.length>0 && (
          <div style={{marginBottom:14}}>
            <div style={{color:INK.faint,fontSize:9,fontFamily:MONO,marginBottom:6}}>ANSWERS</div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              {answers.map(([q,v],i)=>(
                <div key={i} style={{display:"flex",gap:8,fontSize:11.5}}>
                  <span style={{color:INK.faint,minWidth:110}}>{q}</span>
                  <span style={{color:INK.secondary,fontWeight:600}}>{String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {attachments.length>0 && (
          <div>
            <div style={{color:INK.faint,fontSize:9,fontFamily:MONO,marginBottom:6}}>ATTACHMENTS · METADATA ONLY, NOT STORED</div>
            {attachments.map((f,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:8,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",marginBottom:4}}>
                <IconPaperclip size={13} color={C.AMBER}/>
                <span style={{flex:1,fontSize:11,color:INK.secondary}}>{f.name}</span>
                <span style={{fontSize:9.5,color:INK.whisper,fontFamily:MONO}}>{f.size || "—"}</span>
                <Badge state="storage_pending" small/>
              </div>
            ))}
          </div>
        )}
      </GlowCard>

      <GlowCard style={{ padding:"16px 18px" }}>
        <Kicker style={{marginBottom:10}}>Actions</Kicker>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          <ActionBtn accent={C.CYAN} onClick={()=>{ if (!busy) onUpdateStatus(s.id,"pending"); }}>Mark in Review</ActionBtn>
          <ActionBtn accent={C.GREEN} onClick={()=>{ if (!busy) onUpdateStatus(s.id,"resolved"); }}>Resolve</ActionBtn>
          <ActionBtn accent={INK.muted} onClick={()=>{ if (!busy) onUpdateStatus(s.id,"closed"); }}>Close</ActionBtn>
          <ActionBtn accent={C.TEAL} onClick={()=>{ if (!busy) onConvert(s.id); }}>{busy ? "Working…" : "Convert to Project →"}</ActionBtn>
          <ActionBtn accent={C.PURPLE} onClick={()=>flash(`Create task → planned, needs project board bridge`)}>Create Task</ActionBtn>
          <ActionBtn accent={C.CYAN} onClick={copySummary}>{copied?<span style={{display:"inline-flex",alignItems:"center",gap:5}}>Copied <IconCheck size={12} color={C.CYAN}/></span>:"Copy Summary"}</ActionBtn>
          <ActionBtn accent={C.AMBER} onClick={()=>flash(`Request more info → SETUP REQUIRED (no outbound comms provider yet)`)}>Request More Info</ActionBtn>
          <ActionBtn accent={C.PURPLE} onClick={()=>flash(`Assign to agent → PLANNED (command→agent bridge not wired)`)}>Assign to Agent</ActionBtn>
        </div>
        <div style={{marginTop:12,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <LiveBadge source="sq_support_requests" small/><span style={{fontSize:9.5,color:INK.whisper}}>mark in review / resolve / close / convert to project call the live intake API and are audited (olv audit trail)</span>
        </div>
        <div style={{marginTop:4,display:"flex",gap:8,flexWrap:"wrap"}}>
          <Badge state="setup_required" small/><span style={{fontSize:9.5,color:INK.whisper}}>request more info (no comms provider connected)</span>
        </div>
        <div style={{marginTop:4,display:"flex",gap:8,flexWrap:"wrap"}}>
          <Badge state="planned" small/><span style={{fontSize:9.5,color:INK.whisper}}>assign to agent / create task (command→agent bridge, board bridge not wired)</span>
        </div>
      </GlowCard>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// RIGHT RAIL — agent territory by default; manual control in Manage Invites
// ══════════════════════════════════════════════════════════════════════════
function RailSection({ title, children }){
  return <div style={{marginBottom:18}}><Kicker style={{marginBottom:9}}>{title}</Kicker>{children}</div>;
}
// The real receipt vocabulary the intake routes write server-side —
// presented as documentation, NOT as a live activity feed (none is wired).
function AuditVocabulary(){
  return (
    <RailSection title="Audit Receipts · Server-Side">
      {AUDIT_ACTIONS.map((r,i)=>(
        <div key={i} style={{fontSize:10,color:INK.tertiary,fontFamily:MONO,padding:"5px 0",borderBottom:"1px solid rgba(255,255,255,0.03)"}}>{r}</div>
      ))}
      <div style={{marginTop:7,fontSize:9,color:INK.whisper,lineHeight:1.5}}>Every create / revoke / decide / convert writes one of these to the olv audit trail. An in-workroom live feed of them is not wired yet — nothing here is simulated activity.</div>
    </RailSection>
  );
}
function AgentDesk({ suggestions }){
  return (
    <RailSection title="Agents">
      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:9}}>
        <IconAgent size={16} color={C.PURPLE}/>
        <span style={{fontSize:11,color:INK.muted,lineHeight:1.5}}>Spin one up, or keep working solo — nothing runs until you ask.</span>
      </div>
      {suggestions.map((s,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:9,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",marginBottom:6}}>
          <span style={{flex:1,fontSize:10.5,color:INK.secondary}}>{s}</span>
          <Badge state="planned" small/>
        </div>
      ))}
    </RailSection>
  );
}

function RightRail({ mode, selectedInvite, invites, selectedSubmission }){
  if (mode==="send") {
    return (
      <div style={{width:300,flexShrink:0,padding:"18px 16px",overflow:"auto",borderLeft:"1px solid rgba(255,255,255,0.05)"}}>
        <AgentDesk suggestions={["Draft discovery questions for this project type","Summarize the thread so far"]}/>
        <RailSection title="Send Path · What Happens Next">
          {[
            { t:"Their workspace", d:"Sending creates a real sq_intake_invites row; your message is stored as its description and renders on the public portal at /square/intake/:code. No email is sent — delivery is manual copy/share until an email provider is wired.", s:"powered" },
            { t:"Public submission", d:"The client replies through the same public link — no login needed. Replies land in sq_support_requests and appear in the Review Queue.", s:"powered" },
            { t:"Convert to project", d:"Convert to Project creates a real sq_projects row from a submission and marks it resolved. Audited.", s:"powered" },
            { t:"Push updates later", d:"No workspace message backend exists — the portal shows the stored description only. Revoke and re-send to change what they see.", s:"setup_required" },
            { t:"File storage", d:"Attachment names/sizes/types travel with the submission; content upload lands with the storage route.", s:"storage_pending" },
          ].map((x,i)=>(
            <div key={i} style={{marginBottom:10,paddingLeft:10,borderLeft:`2px solid ${a(STATE_META[x.s].color,40)}`}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}><span style={{fontSize:11.5,fontWeight:800,color:INK.secondary}}>{x.t}</span><Badge state={x.s} small/></div>
              <div style={{fontSize:10,color:INK.muted,lineHeight:1.55}}>{x.d}</div>
            </div>
          ))}
        </RailSection>
        <AuditVocabulary/>
        <div style={{fontSize:8,color:INK.whisper,fontFamily:MONO,marginTop:6}}>wired: sq_intake_invites · sq_support_requests · olv audit trail</div>
      </div>
    );
  }
  if (mode==="manage") {
    return (
      <div style={{width:300,flexShrink:0,padding:"18px 16px",overflow:"auto",borderLeft:"1px solid rgba(255,255,255,0.05)"}}>
        <RailSection title="Client Workspaces">
          <div style={{fontSize:11,color:INK.muted,lineHeight:1.6}}>Rows read sq_intake_invites directly. Click one to open that client's full workspace — stored message, live link controls, and lifecycle panel. Revoke closes the public link for real (status → closed, audited).</div>
        </RailSection>
        <AuditVocabulary/>
      </div>
    );
  }
  if (mode==="workspace") {
    const inv = invites.find(i=>i.invite_code===selectedInvite);
    if (!inv) return null;
    return (
      <div style={{width:300,flexShrink:0,padding:"18px 16px",overflow:"auto",borderLeft:"1px solid rgba(255,255,255,0.05)"}}>
        <InviteTimeline inv={inv}/>
        <AgentDesk suggestions={["Draft the next update message","Summarize workspace activity so far"]}/>
      </div>
    );
  }
  if (mode==="queue") {
    return (
      <div style={{width:300,flexShrink:0,padding:"18px 16px",overflow:"auto",borderLeft:"1px solid rgba(255,255,255,0.05)"}}>
        <AgentDesk suggestions={["Triage the queue by urgency","Draft a reply for the selected submission"]}/>
        <AuditVocabulary/>
      </div>
    );
  }
  if (mode==="detail" && selectedSubmission) {
    return (
      <div style={{width:300,flexShrink:0,padding:"18px 16px",overflow:"auto",borderLeft:"1px solid rgba(255,255,255,0.05)"}}>
        <RailSection title="Next Best Action">
          <div style={{fontSize:12,fontWeight:700,color:C.CYAN,marginBottom:4}}>Convert to project</div>
          <div style={{fontSize:10.5,color:INK.muted,lineHeight:1.55}}>Convert to Project creates a real sq_projects row from this submission and marks it resolved.</div>
        </RailSection>
        <AgentDesk suggestions={["Draft a scoping summary from this submission","Suggest a first response to the client"]}/>
        <RailSection title="Record">
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}><LiveBadge source="sq_support_requests" small/></div>
          <div style={{fontSize:9.5,color:INK.whisper,lineHeight:1.55}}>Status decisions and conversion on this submission are audited server-side (intake_submission_decided / intake_submission_converted_to_project → olv audit trail).</div>
        </RailSection>
      </div>
    );
  }
  return null;
}

// ══════════════════════════════════════════════════════════════════════════
// BOTTOM COMMAND COMPOSER
// ══════════════════════════════════════════════════════════════════════════
function BottomComposer({ flash }){
  const [cmd, setCmd] = useState("");
  const submit = () => {
    const raw = cmd.trim();
    if (!raw) return;
    flash(`"${raw}" → discovery agent bridge is PLANNED (not wired) — no fake response generated`);
    setCmd("");
  };
  const suggestions = ["Create link from prompt","Find submissions","Summarize selected","Convert to project"];
  return (
    <div style={{ flexShrink:0, padding:"9px 22px 12px", borderTop:`1px solid ${a(C.CYAN,10)}`, background:"rgba(10,11,16,0.92)", backdropFilter:"blur(16px)" }}>
      <div style={{ display:"flex", gap:6, marginBottom:6 }}>
        {suggestions.map((s,i)=>(
          <div key={i} onClick={()=>setCmd(s)} style={{ padding:"3px 10px", borderRadius:6, fontSize:9.5, fontWeight:600, color:INK.muted, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)", cursor:"pointer" }}>{s}</div>
        ))}
        <div style={{marginLeft:"auto"}}><Badge state="planned" small/></div>
      </div>
      <div style={{ display:"flex", gap:8 }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8, padding:"9px 14px", borderRadius:10, background:"rgba(255,255,255,0.04)", border:`1px solid ${a(C.CYAN,12)}` }}>
          <IconSparkle size={13} color={C.CYAN}/>
          <input value={cmd} onChange={e=>setCmd(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} placeholder="Ask about this discovery, create a link, find a submission…"
            style={{ flex:1, background:"none", border:"none", outline:"none", color:INK.secondary, fontSize:13, fontFamily:FONT }}/>
        </div>
        <ActionBtn accent={C.CYAN} onClick={submit}>Send</ActionBtn>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CLIENT PORTAL PREVIEW (reference — /square/intake/:code, preserved)
// Shows what the client actually sees: your message, in their workspace —
// not a generic "submission received" screen, since the operator messages
// first in this model.
// ══════════════════════════════════════════════════════════════════════════
function ClientPortalPreview({ invite, onClose }){
  const msg = invite && invite.description ? invite.description : "Kicking off your project — more soon.";
  const typeMeta = invite && invite.invite_type ? INVITE_TYPE_META[invite.invite_type] : null;
  const projectType = typeMeta ? PROJECT_TYPES.find(t=>t.api===invite.invite_type) : null;
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:100, background:"rgba(2,2,6,0.85)", backdropFilter:"blur(14px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:460, borderRadius:20, background:"#0d0e16", border:`1px solid ${a(C.TEAL,22)}`, padding:"32px 28px", textAlign:"center", boxShadow:`0 30px 70px rgba(0,0,0,0.5)` }}>
        <div style={{fontSize:10,letterSpacing:"0.2em",color:INK.faint,fontWeight:800,fontFamily:MONO,marginBottom:14}}>SQUARE CODE · YOUR PRIVATE WORKSPACE</div>
        {projectType && <div style={{display:"flex",justifyContent:"center",marginBottom:10}}><projectType.Icon size={38} color={projectType.color}/></div>}
        <div style={{fontSize:19,fontWeight:900,color:INK.primary,marginBottom:8}}>{invite ? invite.title : "Your Project"}</div>
        <div style={{fontSize:11,color:INK.muted,marginBottom:16}}>Reference code</div>
        <div style={{fontSize:14,fontWeight:900,color:C.TEAL,fontFamily:MONO,marginBottom:18,wordBreak:"break-all"}}>{invite ? invite.invite_code : "—"}</div>
        <div style={{textAlign:"left",padding:"12px 14px",borderRadius:10,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",marginBottom:18}}>
          <div style={{fontSize:9,color:INK.faint,fontFamily:MONO,marginBottom:5}}>MESSAGE FROM THE TEAM</div>
          <div style={{fontSize:12,color:INK.secondary,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{msg}</div>
        </div>
        <div style={{fontSize:11.5,color:INK.tertiary,lineHeight:1.7,marginBottom:16,textAlign:"left"}}>
          <strong style={{color:INK.secondary}}>What this is:</strong> a local mock-up of their portal page. The real page is live at the link — open it to see exactly what they see. No account or login is required on their side.
        </div>
        <div style={{fontSize:9.5,color:INK.whisper,lineHeight:1.6}}>Secured by Square Code · governed by Olivium</div>
        <div onClick={onClose} style={{marginTop:20,fontSize:10.5,fontWeight:700,color:INK.muted,cursor:"pointer"}}>Close preview</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// APP
// ══════════════════════════════════════════════════════════════════════════
// Honest data states for the list-backed modes — no fabricated rows, ever.
function DataStatePanel({ loading, error, authRequired, onRetry }){
  if (loading) return (
    // Loading — spatial, not boxed: the status breathes on the ambient canvas.
    <div style={{ padding:"64px 24px", textAlign:"center", maxWidth:520, margin:"0 auto" }}>
      <div style={{fontSize:13,color:INK.secondary,lineHeight:1.7}} role="status" aria-live="polite">Loading your invites and submissions…</div>
      <div style={{fontSize:10.5,color:INK.whisper,fontFamily:MONO,marginTop:6}}>live intake backend · sq_intake_invites · sq_support_requests</div>
    </div>
  );
  if (authRequired) return (
    <GlowCard accent={C.AMBER} active style={{ padding:"28px 24px", maxWidth:820, margin:"0 auto" }}>
      <Kicker style={{marginBottom:8, color:a(C.AMBER,80)}}>Sign In Required</Kicker>
      <div style={{fontSize:12,color:INK.secondary,lineHeight:1.7}}>The discovery workroom reads sq_intake_invites and sq_support_requests through the authenticated intake API. Sign in to the cockpit to load your real invites and submissions — nothing is fabricated here.</div>
    </GlowCard>
  );
  if (error) return (
    <GlowCard accent={C.RED} active style={{ padding:"28px 24px", maxWidth:820, margin:"0 auto" }}>
      <Kicker style={{marginBottom:8, color:a(C.RED,80)}}>Intake Backend Error</Kicker>
      <div style={{fontSize:12,color:INK.secondary,lineHeight:1.7,marginBottom:12}}>{error}</div>
      <ActionBtn accent={C.RED} onClick={onRetry}>Retry</ActionBtn>
    </GlowCard>
  );
  return null;
}

export default function ClientDiscoveryWorkroom({ onExit, onHome, onNavigate } = {}){
  const { invites, submissions, loading, error, authRequired, reload,
    createInvite, revokeInvite, decideSubmission, convertToProject, createClientProject } = useDiscovery();

  const [mode, setMode] = useState("send");
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [thread, setThread] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const flash = (m) => { setToast(m); clearTimeout(toastTimer.current); toastTimer.current = setTimeout(()=>setToast(null), 2800); };

  const queueCount = submissions.filter(s=>s.status==="open").length;
  const selectedSubmission = useMemo(()=>submissions.find(s=>String(s.id)===String(selectedSubmissionId)) || null, [submissions, selectedSubmissionId]);
  const selectedInviteObj = useMemo(()=>invites.find(i=>i.invite_code===selectedInvite) || null, [invites, selectedInvite]);

  const openSubmission = (id) => { setSelectedSubmissionId(id); setMode("detail"); };
  const backToQueue = () => { setSelectedSubmissionId(null); setMode("queue"); };
  const switchMode = (m) => { setSelectedInvite(null); setMode(m); };
  const openWorkspace = (code) => { setSelectedInvite(code); setMode("workspace"); };
  const backToManage = () => { setSelectedInvite(null); setMode("manage"); };

  // REAL create — POST /api/square/command-center/intake-invites.
  // The hook reloads the invites list, so Manage Invites shows the new
  // row immediately. Errors bubble to SendIntake's inline error state.
  const handleSend = async (draft) => {
    const result = await createInvite({ title: draft.title, invite_type: draft.invite_type, description: draft.description });
    const inv = result.invite || {};
    setGeneratedInvite({ ...inv, public_path: result.public_path, recipientEmail: draft.recipientEmail });
    flash(`Client workspace created for ${inv.title || draft.title} — live sq_intake_invites row (audited). Copy the link to share it.`);
    return result;
  };

  // REAL revoke — status → closed; the public link stops accepting.
  const handleRevoke = async (inviteId, code) => {
    if (busy) return;
    setBusy(true);
    try {
      await revokeInvite(inviteId);
      flash(`Invite ${code} revoked — its public link no longer accepts submissions (status → closed, audited)`);
    } catch (err) {
      flash(`Revoke failed: ${humanizeDiscoveryError(err)}`);
    } finally { setBusy(false); }
  };

  // REAL decide — 'pending' (in review) | 'resolved' | 'closed'.
  const updateSubmissionStatus = async (id, decision) => {
    if (busy) return;
    setBusy(true);
    try {
      await decideSubmission(id, decision);
      flash(`Submission → ${QUEUE_STATUS_LABEL[decision] || decision} — saved to sq_support_requests (audited)`);
    } catch (err) {
      flash(`Status update failed: ${humanizeDiscoveryError(err)}`);
    } finally { setBusy(false); }
  };

  // REAL convert — creates an sq_projects row; navigation goes through
  // the onNavigate prop wired by the route wrapper.
  const handleConvert = async (id) => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await convertToProject(id);
      if (result && result.ok && result.converted) {
        flash(result.message || "Project created from this submission (audited).");
        if (result.navigate && onNavigate) onNavigate(result.navigate);
      } else {
        flash((result && (result.message || result.error)) || "Convert failed.");
      }
    } catch (err) {
      flash(`Convert failed: ${humanizeDiscoveryError(err)}`);
    } finally { setBusy(false); }
  };

  // REAL clipboard write of the real public link, built at click time.
  const copyInviteLink = (code) => {
    const url = publicIntakeUrl(code);
    try { navigator.clipboard?.writeText(url).catch(()=>{}); } catch(e) {}
    flash(`Link copied — ${url}`);
  };
  const viewSubmissions = (code) => { setSelectedInvite(null); setMode("queue"); };

  const dataBlocked = loading || authRequired || error;

  return (
    <div style={{ width:"100%", height:"100vh", overflow:"hidden", display:"flex", flexDirection:"column", position:"relative", fontFamily:FONT, background:"#0a0b10" }}>
      <Substrate/>
      <TopBar queueCount={queueCount} onHome={onHome} onBack={onExit || (()=>flash("← Command Center (pass onExit to wire); production intake stays at /square/command-center/operations/client-intake"))}/>

      <div style={{ position:"relative", zIndex:1, flexShrink:0, padding:"14px 22px 0" }}>
        <ModeTabs mode={mode==="detail"?"queue":mode==="workspace"?"manage":mode} setMode={switchMode} queueCount={queueCount}/>
        <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <span style={{fontSize:9.5,color:INK.whisper,fontFamily:MONO}}>Invites and submissions load from the live intake backend (sq_intake_invites · sq_support_requests). Link delivery is manual copy/share — no email provider is wired.</span>
        </div>
      </div>

      <div style={{ position:"relative", zIndex:1, flex:1, minHeight:0, display:"flex", overflow:"hidden" }}>
        <div style={{ flex:1, minWidth:0, overflow:"auto", padding:"22px 26px" }}>
          {mode==="send" && <SendIntake onSend={handleSend} generatedInvite={generatedInvite} onPreviewPublic={()=>setPreviewOpen(true)} onGoToWorkspace={()=>generatedInvite && openWorkspace(generatedInvite.invite_code)} thread={thread} setThread={setThread} authRequired={authRequired}/>}
          {mode==="create" && <CreateClientProject onCreate={createClientProject} authRequired={authRequired} onNavigate={onNavigate}/>}
          {mode!=="send" && mode!=="create" && dataBlocked && <DataStatePanel loading={loading} error={error} authRequired={authRequired} onRetry={reload}/>}
          {mode==="manage" && !dataBlocked && <ManageInvites invites={invites} onOpen={openWorkspace} onRevoke={handleRevoke} onCopyLink={copyInviteLink} onViewSubmissions={viewSubmissions}/>}
          {mode==="workspace" && !dataBlocked && selectedInviteObj && <InviteWorkspace invite={selectedInviteObj} onBack={backToManage} onRevoke={handleRevoke} onCopyLink={copyInviteLink} submissions={submissions} onOpenSubmission={openSubmission}/>}
          {mode==="queue" && !dataBlocked && <ReviewQueue submissions={submissions} onOpen={openSubmission}/>}
          {mode==="detail" && !dataBlocked && selectedSubmission && <SubmissionDetail submission={selectedSubmission} onBack={backToQueue} onUpdateStatus={updateSubmissionStatus} onConvert={handleConvert} busy={busy} flash={flash}/>}
        </div>
        <RightRail mode={mode} selectedInvite={selectedInvite} invites={invites} selectedSubmission={selectedSubmission}/>
      </div>

      <BottomComposer flash={flash}/>

      {toast && (
        <div style={{ position:"fixed", bottom:64, left:"50%", transform:"translateX(-50%)", zIndex:150, padding:"9px 20px", borderRadius:10, background:a(C.PURPLE,16), border:`1px solid ${a(C.PURPLE,28)}`, backdropFilter:"blur(12px)", fontSize:11, color:"#c4a9ea", fontWeight:600, fontFamily:MONO, whiteSpace:"nowrap" }}>{toast}</div>
      )}
      {previewOpen && <ClientPortalPreview invite={generatedInvite} onClose={()=>setPreviewOpen(false)}/>}

      <style>{`
        @keyframes icon-blink { 0%,100%{opacity:1} 50%{opacity:0.15} }
        @keyframes icon-pulse-ring { 0%{opacity:0.5;transform:scale(1)} 100%{opacity:0;transform:scale(1.35)} }
        @keyframes icon-rotate { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes icon-draw { to{stroke-dashoffset:0} }
        textarea::placeholder, input::placeholder { color: ${INK.whisper}; }
        ::-webkit-scrollbar { width:4px; height:4px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.08); border-radius:2px; }
      `}</style>
    </div>
  );
}


